import { BoxButton, Description, Divider, List, Svg, Table, TableCell, Tooltip } from '@repo/ui';
import { useRouter } from 'next/router';

import { ResultTableRow } from '@/components/tax-refund/apply/result/ResultTableRow';
import ApplyResultFaqView from '@/components/tax-refund/common/view/ApplyResultFaqView';
import ApplyResultInfoView from '@/components/tax-refund/common/view/ApplyResultInfoView';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { ApplyResultData } from '@/lib/types/request';
import { formatAccountBankNo } from '@/lib/utils/stringFormat';

export const ApplyResultRefundDecisionContent = ({ data }: { data: ApplyResultData }) => {
  const isCorps = data.businessType === 'corps';
  const displayName = isCorps ? (data.fullName ?? data.name) : data.name;
  const router = useRouter();
  const { hasRegisteredCard, cardName, cardLogo, cardNumber } = data.paymentCard;

  const infoContents = isCorps ? corporationInfoContents : individualInfoContents(hasRegisteredCard ?? false);
  const faqContents = isCorps ? corporationFaqContents : individualFaqContents;

  const title = isCorps ? `’${displayName}’의\n환급금이 곧 입금될 예정이에요!` : `${displayName}님의 환급금이\n곧 입금될 예정이에요!`;

  const handleCardClick = () => {
    if (!isCorps) {
      router.push(COMMON_PATHS.환급결제카드입력);
    }
  };

  const paymentValue =
    data.paymentAmount === '' ? (
      '확인중...'
    ) : isCorps ? (
      <Tooltip content={'환급금 입금 후 결제가 필요해요'} side={'right'}>
        data.paymentAmount
      </Tooltip>
    ) : (
      data.paymentAmount
    );

  return (
    <div className={'pb-normal-medium'}>
      <div className="px-4.5 py-normal-small">
        <Description
          title={title}
          size={'medium'}
          description={'입금은 환급 결정일부터 3일 정도 소요돼요.\n환급 계좌를 확인해주세요.'}
          graphic={<Svg src="icg_bankbook-arrow-left_md.svg" width={80} height={80} />}
        />
      </div>
      <Divider size={'large'} />
      <div className="px-4.5 py-tiny-large">
        <div className="pb-normal-large">
          <div className="body-large-regular text-neutral-foreground-low pt-normal-large">{isCorps ? '법인세 결정 환급액' : '종합소득세 결정 환급액'}</div>
          <div className="text-neutral-foreground-main heading-medium-bold">{data.refundAmount}</div>
          <Divider className="my-normal-medium" />
          <ResultTableRow label={'환급 신청일'} value={data.applyDate} />
          <ResultTableRow label={'환급 결정일'} value={data.resultDate} />
          <ResultTableRow label={'입금 예정일'} value={data.depositExpectDate} />
          <ResultTableRow label={'환급 계좌'} value={formatAccountBankNo(data.applicationInfo?.bank, data.applicationInfo?.accountNo)} />
          <ResultTableRow label={'이용료'} value={paymentValue} emphasize={data.paymentAmount !== ''} />
        </div>
        <ApplyResultInfoView data={infoContents} />
        <Divider />
        {!isCorps && hasRegisteredCard && (
          <Table
            headerCell={
              <TableCell className={'flex items-center'} variant={'header'}>
                결제 카드
              </TableCell>
            }
            contentCell={
              <>
                <TableCell className={'flex items-center flex-1 justify-end'}>
                  <div className={'flex gap-tiny-medium text-[15px]'}>
                    <Svg src={cardLogo ?? ''} width={24} height={24} />
                    {cardName}({cardNumber.slice(-4)})
                  </div>
                </TableCell>
                <TableCell>
                  <BoxButton size={'small'} className={'ml-3'} variant={'tonal-secondary'} onClick={handleCardClick}>
                    변경
                  </BoxButton>
                </TableCell>
              </>
            }
          />
        )}
        <div className={'pt-tiny-large'}>
          <List
            title={'환급이 결정되어 취소할 수 없어요.'}
            size={'small'}
            primaryAction
            state={'disabled'}
            supportingElement={
              <div className={'flex items-center opacity-40 body-small-medium'}>
                신청 취소
                <Svg src={'ic_chevron-right.svg'} width={18} height={18} />
              </div>
            }
          />
        </div>
      </div>
      <Divider size={'large'} />
      <ApplyResultFaqView data={faqContents} clickEventName={`${isCorps ? 'corps' : 'indis'}-result_faq_refund-decision`} />
    </div>
  );
};

const individualInfoContents = (hasRegisteredCard: boolean) => [
  { content: '환급금은 국세청 또는 관할세무서명으로 입금돼요.' },
  { content: '환급금 입금은 환급 결정일로부터 최소 1일에서 최대 14일 소요돼요.' },
  { content: hasRegisteredCard ? '환급이 완료되면 등록된 결제 카드로 이용료가 자동 결제돼요.' : '입금이 완료되면 이용료 결제 안내가 전송돼요.' }
];

const corporationInfoContents = [
  { content: '환급이 완료되면 이용료 결제 안내가 메시지로 전송됩니다.' },
  { content: '환급금은 국세청 또는 관할세무서명으로 입금됩니다.' }
];

const individualFaqContents = [
  {
    title: '농어촌 특별세 납부고지서가 왔는데 납부해야 하나요?',
    content:
      '농어촌 특별세 납부고지서를 받으셨다면 특정 조세 혜택이 잘 적용되어 환급 신청이 완료된 경우입니다.\n비즈넵 환급에서는 농어촌 특별세를 신고할 때 환급금에서 충당되도록 충당서를 함께 발송하기 때문에 따로 납부하지 않으셔도 됩니다.'
  },
  {
    title: '결제는 어떻게 하나요?',
    content: '환급 과정이 마무리되어 곧 카카오톡 알림톡이나 문자 메시지로 결제 안내를 발송해드릴 예정입니다. 안내된 방법으로 결제를 진행해주세요.'
  },
  {
    title: '환급액이 처음 조회했을 때와 달라요.',
    content: '환급액은 제휴 세무사와 국세청 검토 후 결정되며, 검토 과정에서 일부 변동될 수 있습니다.'
  },
  {
    title: '환급 결정 후 3일이 지났는데 아직 환급금이 입금되지 않았어요.',
    content:
      '환급금 입금은 환급 결정 후 최대 14일까지 소요될 수 있습니다. 혹시 이미 환급금 지급이 되었는지 입금자명을 다시 한 번 확인해주세요. 환급금은 관할세무서명으로 입금됩니다.'
  },
  {
    title: '이용료는 어떤 기준으로 책정되는 건가요?',
    content:
      '비즈넵 환급 서비스 이용 시 발생하는 이용료는 비즈넵 환급 제휴 세무사의 신고대리에 따른 비용으로 업계 평균 이용료로 책정되었습니다.\n비즈넵 환급을 이용하면 업계 평균 이용료로 추가 서류 제출 없이 쉽고 간편하게 환급받을 수 있습니다.'
  },
  {
    title: '현금수령통지서를 받았어요.',
    content:
      '비즈넵 환급은 신청 시 입력하신 계좌번호를 기재해 신고를 접수하고 있습니다. 다만, 국세청 담당 공무원의 검토 과정 중 계좌 오류가 발생하거나, 납세자의 기타 상황에 따라서 현금수령통지서가 발송될 수 있습니다. 환급금은 현금수령통지서에 기재되어 있는 방법으로 수령이 가능합니다.'
  }
];

const corporationFaqContents = [
  {
    title: '농어촌 특별세 납부고지서가 왔는데 납부해야 하나요?',
    content:
      '농어촌 특별세는 특정 조세 혜택을 받은 환급 건에 적용되는 세금으로 혜택 금액의 20%를 납부해야 합니다. 농어촌 특별세 납부고지서를 받으셨다면 특정 조세 혜택이 잘 적용되어 환급 신청이 완료된 경우입니다. 비즈넵 환급에서는 농어촌 특별세를 반영해 신고하고 있으며 농어촌 특별세로 고지된 금액보다 더 큰 금액으로 환급이 이루어지니 고지서를 확인해 납부해주시면 됩니다.'
  },
  {
    title: '결제는 어떻게 하나요?',
    content: '환급 과정이 마무리되어 곧 카카오톡 알림톡이나 문자 메시지로 결제 안내를 발송해드릴 예정입니다. 안내된 방법으로 결제를 진행해주세요.'
  },
  {
    title: '환급액이 처음 조회했을 때와 달라요.',
    content: '환급액은 제휴 세무사와 국세청 검토 후 결정되며, 검토 과정에서 일부 변동될 수 있습니다.'
  },
  {
    title: '환급 결정 후 3일이 지났는데 아직 환급금이 입금되지 않았어요.',
    content:
      '환급금 입금은 환급 결정 후 최대 일주일까지 소요될 수 있습니다. 혹시 이미 환급금 지급이 되었는지 입금자명을 다시 한 번 확인해주세요. 환급금은 관할세무서명으로 입금됩니다.'
  },
  {
    title: '이용료는 어떤 기준으로 책정되는 건가요?',
    content:
      '비즈넵 환급 서비스 이용 시 발생하는 이용료는 비즈넵 환급 제휴 세무사의 신고대리에 따른 비용으로 업계 평균 이용료로 책정되었습니다.\n비즈넵 환급을 이용하면 업계 평균 이용료로 추가 서류 제출 없이 쉽고 간편하게 환급받을 수 있습니다.'
  },
  {
    title: '현금수령통지서를 받았어요.',
    content:
      '비즈넵 환급은 신청 시 입력하신 계좌번호를 기재해 신고를 접수하고 있습니다. 다만, 국세청 담당 공무원의 검토 과정 중 계좌 오류가 발생하거나, 납세자의 기타 상황에 따라서 현금수령통지서가 발송될 수 있습니다. 환급금은 현금수령통지서에 기재되어 있는 방법으로 수령이 가능합니다.'
  }
];
